import { useCallback, useEffect, useState } from "react";
import { fetchCompanyAdmissions } from "../api/companyApi";
import type { CompanyAdmissionApi, CompanyAdmissionsQuery } from "../api/types";
import { enterpriseAdmissions } from "../data";
import { getAuthToken } from "../auth";
import type { Admission } from "../types";

export function mapApiAdmission(row: CompanyAdmissionApi): Admission {
  return {
    id: String(row.id),
    date: row.date,
    time: row.time,
    patient: row.patient,
    patientSub: row.patient_sub,
    patientType: row.patient_type,
    hospital: row.hospital,
    company: row.company,
    status: row.status,
    motif: row.motif,
  };
}

export function useEnterpriseAdmissions(filters: CompanyAdmissionsQuery = {}) {
  const [admissions, setAdmissions] = useState<Admission[]>(() => [...enterpriseAdmissions]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);
  const [total, setTotal] = useState(enterpriseAdmissions.length);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setAdmissions([...enterpriseAdmissions]);
      setTotal(enterpriseAdmissions.length);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const response = await fetchCompanyAdmissions(filters);
      const mapped = (response.admissions ?? []).map(mapApiAdmission);
      setAdmissions(mapped);
      setTotal(response.total ?? mapped.length);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Impossible de charger les admissions.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.date_start, filters.date_end, filters.search, filters.hospital_id]);

  useEffect(() => {
    void load();
  }, [load]);

  return { admissions, total, loading, error, usingApi, reload: load };
}
